import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { money, number, thaiDateTime } from '../lib/format'

/**
 * Event store console (part 72).
 *
 * The page reads the per-aggregate log, not the audit trail: an aggregate is a small account
 * whose state is folded from account.opened/credited/debited/frozen/unfrozen/closed events.
 * Loading at a version and replaying to a version are different buttons on purpose — the first
 * reads a historical state without writing a job, the second records what version N looked like.
 *
 * expected_version is shown and editable. That is deliberate: an operator demonstrating
 * optimistic concurrency can leave a stale number in place and see the 409 the API returns,
 * instead of the screen silently replacing it on every refresh.
 *
 * The API mount (MountEventStoreRoutes) is intentionally not called from server.go in this part
 * and this page is not routed from App.tsx; both are one-line wiring changes, reviewed when the
 * first operator needs the console.
 */

type AccountStatus = 'active' | 'frozen' | 'closed' | ''

interface EventRow {
  id: string
  aggregate_id: string
  aggregate_type: string
  version: number
  type: string
  payload: Record<string, unknown>
  metadata?: Record<string, unknown>
  actor_id?: string
  occurred_at: string
  recorded_at?: string
}

interface AccountState {
  aggregate_id: string
  aggregate_type: string
  version: number
  status: AccountStatus
  owner: string
  currency: string
  balance_satang: number
  last_event_id: string
  last_event_type: string
  updated_at: string
}

interface AggregateView {
  state: AccountState
  current_version: number
  target_version: number
  snapshot_version: number
  events_replayed: number
  note: string
}

interface AggregateHead {
  aggregate_id: string
  aggregate_type: string
  current_version: number
  last_event_id?: string
  snapshot_version: number
  updated_at: string
}

interface AggregateList {
  aggregates: AggregateHead[]
  count: number
  note: string
}

interface EventList {
  aggregate_id: string
  events: EventRow[]
  count: number
  after_version: number
  note: string
}

interface AppendResult {
  event: EventRow
  state: AccountState
  replayed: boolean
  current_version: number
}

interface ReplayJob {
  id: string
  aggregate_id: string
  aggregate_type: string
  from_version: number
  target_version: number
  current_version: number
  events_replayed: number
  status: string
  state?: AccountState
  error?: string
  requested_by?: string
  created_at: string
  completed_at?: string
}

interface ReplayList {
  jobs: ReplayJob[]
  count: number
  note: string
}

const EVENT_TYPES = [
  { id: 'account.opened', label: 'เปิดบัญชี', hint: 'เหตุการณ์แรกของ aggregate เท่านั้น' },
  { id: 'account.credited', label: 'รับเงินเข้า', hint: 'เพิ่มยอดคงเหลือ' },
  { id: 'account.debited', label: 'จ่ายเงินออก', hint: 'ต้องมียอดคงเหลือเพียงพอ' },
  { id: 'account.frozen', label: 'อายัดบัญชี', hint: 'หยุดรับและจ่ายเงิน' },
  { id: 'account.unfrozen', label: 'ปลดอายัด', hint: 'กลับมาใช้งานได้ตามปกติ' },
  { id: 'account.closed', label: 'ปิดบัญชี', hint: 'ยอดคงเหลือต้องเป็นศูนย์' },
] as const

type EventType = (typeof EVENT_TYPES)[number]['id']

const EVENT_LABEL: Record<string, string> = Object.fromEntries(
  EVENT_TYPES.map((row) => [row.id, row.label]),
)

const STATUS_LABEL: Record<string, string> = {
  active: 'ใช้งาน',
  frozen: 'อายัด',
  closed: 'ปิดบัญชี',
  '': 'ยังไม่มีสถานะ',
}

const REPLAY_STATUS_LABEL: Record<string, string> = {
  queued: 'รอคิว',
  running: 'กำลังทำ',
  completed: 'สำเร็จ',
  failed: 'ล้มเหลว',
}

const EMPTY_HEADS: AggregateHead[] = []

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

/** Parse baht as a decimal string: no parseFloat, so 0.1 never becomes 9 satang. */
function bahtToSatang(input: string): number {
  const cleaned = input.trim().replace(/,/g, '')
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) return Number.NaN
  const [baht, fraction = ''] = cleaned.split('.')
  const satang = fraction.padEnd(2, '0').slice(0, 2)
  return Number(baht) * 100 + Number(satang)
}

function eventSummary(event: EventRow): string {
  const amount = event.payload.amount_satang ?? event.payload.opening_balance_satang
  if (typeof amount === 'number') {
    const prefix = event.type === 'account.opened' ? 'ยอดเปิดบัญชี ' : ''
    return `${prefix}${money(amount)}`
  }
  if (event.type === 'account.opened') {
    const owner = event.payload.owner
    return typeof owner === 'string' ? owner : 'เปิดบัญชี'
  }
  return 'ไม่มีจำนวนเงิน'
}

function aggregateShort(id: string): string {
  return id.slice(0, 8)
}

export function EventStore() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const canManage = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager'

  const headsQ = useQuery({
    queryKey: ['eventstore', 'aggregates'],
    queryFn: () => api<AggregateList>('/v1/event-store/aggregates?limit=200'),
    staleTime: 10_000,
  })
  const replaysQ = useQuery({
    queryKey: ['eventstore', 'replays'],
    queryFn: () => api<ReplayList>('/v1/event-store/replays?limit=50'),
    staleTime: 10_000,
  })

  const heads = headsQ.data?.aggregates ?? EMPTY_HEADS
  const [selectedId, setSelectedId] = useState('')
  const [viewVersion, setViewVersion] = useState(0)
  const [creating, setCreating] = useState(false)
  const [newAggregateId, setNewAggregateId] = useState('')

  useEffect(() => {
    if (selectedId || heads.length === 0) return
    setSelectedId(heads[0].aggregate_id)
  }, [heads, selectedId])

  const detailQ = useQuery({
    queryKey: ['eventstore', 'aggregate', selectedId, viewVersion],
    queryFn: () =>
      api<AggregateView>(
        `/v1/event-store/aggregates/${selectedId}${viewVersion > 0 ? `?version=${viewVersion}` : ''}`,
      ),
    enabled: selectedId !== '',
    staleTime: 5_000,
  })
  const eventsQ = useQuery({
    queryKey: ['eventstore', 'events', selectedId],
    queryFn: () => api<EventList>(`/v1/event-store/aggregates/${selectedId}/events?limit=500`),
    enabled: selectedId !== '',
    staleTime: 5_000,
  })

  const selectedHead = useMemo(
    () => heads.find((head) => head.aggregate_id === selectedId) ?? null,
    [heads, selectedId],
  )
  const detail = detailQ.data ?? null
  const events = eventsQ.data?.events ?? []

  const [eventType, setEventType] = useState<EventType>('account.credited')
  const [owner, setOwner] = useState('')
  const [amountBaht, setAmountBaht] = useState('')
  const [expectedVersion, setExpectedVersion] = useState('0')
  const [appendNotice, setAppendNotice] = useState('')
  const [replayTarget, setReplayTarget] = useState('')
  const [replayResult, setReplayResult] = useState<ReplayJob | null>(null)
  const [replayNotice, setReplayNotice] = useState('')

  // Selecting another aggregate resets the append form to that aggregate's head. While the
  // operator is creating a new account, the selection effect stays out of the way.
  useEffect(() => {
    if (creating || !selectedId) return
    const head = heads.find((row) => row.aggregate_id === selectedId)
    if (!head) return
    setExpectedVersion(String(head.current_version))
    setEventType(head.current_version === 0 ? 'account.opened' : 'account.credited')
    setViewVersion(0)
    setReplayResult(null)
    setReplayNotice('')
    setAppendNotice('')
  }, [creating, selectedId, heads])

  useEffect(() => {
    if (!detail) return
    setReplayTarget(String(detail.state.version || 1))
  }, [detail?.state.version, detail?.current_version])

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['eventstore'] })
  }

  const startNew = () => {
    setCreating(true)
    setNewAggregateId(crypto.randomUUID())
    setEventType('account.opened')
    setOwner('')
    setAmountBaht('')
    setExpectedVersion('0')
    setAppendNotice('')
    setReplayResult(null)
    setReplayNotice('')
    setViewVersion(0)
  }

  const appendEvent = useMutation({
    mutationFn: () => {
      const aggregateID = creating ? newAggregateId : selectedId
      if (!aggregateID) throw new Error('เลือกบัญชีหรือสร้างบัญชีใหม่ก่อน')
      const expected = Number(expectedVersion)
      if (!Number.isInteger(expected) || expected < 0) {
        throw new Error('expected_version ต้องเป็นจำนวนเต็มตั้งแต่ 0 ขึ้นไป')
      }

      let payload: Record<string, unknown> = {}
      if (eventType === 'account.opened') {
        if (!owner.trim()) throw new Error('กรอกชื่อผู้ถือบัญชี')
        const opening = amountBaht.trim() === '' ? 0 : bahtToSatang(amountBaht)
        if (!Number.isFinite(opening) || opening < 0) throw new Error('ยอดเปิดบัญชีไม่ถูกต้อง')
        payload = { owner: owner.trim(), opening_balance_satang: opening, currency: 'THB' }
      } else if (eventType === 'account.credited' || eventType === 'account.debited') {
        const amount = bahtToSatang(amountBaht)
        if (!Number.isFinite(amount) || amount <= 0) throw new Error('จำนวนเงินไม่ถูกต้อง')
        payload = { amount_satang: amount }
      }

      return apiWrite<AppendResult>(`/v1/event-store/aggregates/${aggregateID}/events`, 'POST', {
        event_id: crypto.randomUUID(),
        aggregate_type: 'account',
        expected_version: expected,
        type: eventType,
        payload,
      })
    },
    onSuccess: (result) => {
      setCreating(false)
      setSelectedId(result.event.aggregate_id)
      setExpectedVersion(String(result.current_version))
      setAmountBaht('')
      setViewVersion(0)
      setAppendNotice(
        result.replayed
          ? `เหตุการณ์ ${aggregateShort(result.event.id)} มีอยู่แล้ว ระบบจึงไม่บันทึกซ้ำ (เวอร์ชัน ${result.current_version})`
          : `บันทึกเหตุการณ์เวอร์ชัน ${result.event.version} แล้ว สถานะล่าสุดคือเวอร์ชัน ${result.current_version}`,
      )
      invalidate()
    },
  })

  const replay = useMutation({
    mutationFn: () => {
      if (!selectedId) throw new Error('เลือกบัญชีก่อนเริ่ม replay')
      const target = Number(replayTarget)
      if (!Number.isInteger(target) || target < 1) {
        throw new Error('เวอร์ชันเป้าหมายต้องเป็นจำนวนเต็มตั้งแต่ 1 ขึ้นไป')
      }
      return apiWrite<ReplayJob>(`/v1/event-store/aggregates/${selectedId}/replays`, 'POST', {
        target_version: target,
      })
    },
    onSuccess: (job) => {
      setReplayResult(job)
      setReplayNotice(
        job.target_version === job.current_version
          ? `ประกอบสถานะเวอร์ชัน ${job.target_version} สำเร็จ ตรงกับสถานะปัจจุบัน`
          : `ประกอบสถานะเวอร์ชัน ${job.target_version} สำเร็จ สถานะปัจจุบันคือเวอร์ชัน ${job.current_version}`,
      )
      invalidate()
    },
  })

  const totalEvents = useMemo(
    () => heads.reduce((sum, head) => sum + head.current_version, 0),
    [heads],
  )
  const latestAt = useMemo(() => {
    if (heads.length === 0) return ''
    return heads.reduce((latest, head) => (head.updated_at > latest ? head.updated_at : latest), heads[0].updated_at)
  }, [heads])

  if (headsQ.isLoading) {
    return (
      <>
        <PageHead title="ที่เก็บเหตุการณ์" sub="บันทึกเหตุการณ์แบบเพิ่มอย่างเดียวและย้อนสถานะได้" />
        <div className="stack"><Skel h={120} /><Skel h={320} /></div>
      </>
    )
  }

  if (headsQ.isError || !headsQ.data) {
    return (
      <>
        <PageHead title="ที่เก็บเหตุการณ์" sub="บันทึกเหตุการณ์แบบเพิ่มอย่างเดียวและย้อนสถานะได้" />
        <Empty
          title="โหลดที่เก็บเหตุการณ์ไม่ได้"
          body="ลองรีเฟรชหน้าอีกครั้ง หรือตรวจสอบการเชื่อมต่อกับเซิร์ฟเวอร์"
        />
      </>
    )
  }

  const formAggregateID = creating ? newAggregateId : selectedId
  const showAmount = eventType === 'account.opened' || eventType === 'account.credited' || eventType === 'account.debited'
  const amountLabel = eventType === 'account.opened' ? 'ยอดเปิดบัญชี (บาท)' : 'จำนวนเงิน (บาท)'

  return (
    <>
      <PageHead
        title="ที่เก็บเหตุการณ์"
        sub="สถานะทุกบัญชีประกอบใหม่จากเหตุการณ์จริง การเพิ่มที่เวอร์ชันล้าสมัยจะถูกปฏิเสธด้วย 409"
        actions={
          <div className="row" style={{ gap: 8 }}>
            <button className="btn" type="button" onClick={startNew} disabled={!canManage}>
              สร้างบัญชีใหม่
            </button>
            <button className="btn" type="button" onClick={() => invalidate()} disabled={headsQ.isFetching}>
              {headsQ.isFetching ? 'กำลังโหลด…' : 'รีเฟรช'}
            </button>
          </div>
        }
      />

      <div className="grid-4">
        <Stat label="aggregate ทั้งหมด" value={<span className="tnum">{number(heads.length)}</span>} unit="บัญชี" />
        <Stat label="เหตุการณ์ที่บันทึก" value={<span className="tnum">{number(totalEvents)}</span>} unit="รายการ" />
        <Stat label="replay ล่าสุด" value={<span className="tnum">{number(replaysQ.data?.count ?? 0)}</span>} unit="งาน" />
        <Stat
          label="อัปเดตล่าสุด"
          value={<span style={{ fontSize: 15 }}>{latestAt ? thaiDateTime(latestAt) : '—'}</span>}
          foot="เวลาที่หัว aggregate ถูกเลื่อนครั้งล่าสุด"
        />
      </div>

      <div className="grid-main" style={{ marginTop: 16 }}>
        <div className="stack">
          <div className="card card-pad">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <div className="card-label">บัญชีในที่เก็บเหตุการณ์</div>
              {creating ? <span className="chip gold">กำลังสร้างบัญชีใหม่</span> : null}
            </div>
            {heads.length === 0 ? (
              <div style={{ marginTop: 14 }}>
                <Empty
                  title="ยังไม่มีบัญชีในระบบ"
                  body="กด «สร้างบัญชีใหม่» เพื่อออกเหตุการณ์ account.opened แรก"
                />
              </div>
            ) : (
              <div className="table-wrap" style={{ marginTop: 14 }}>
                <table className="data">
                  <thead>
                    <tr>
                      <th>บัญชี</th>
                      <th>สถานะ</th>
                      <th className="num">เวอร์ชันปัจจุบัน</th>
                      <th>แก้ไขล่าสุด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {heads.map((head) => {
                      const active = !creating && head.aggregate_id === selectedId
                      return (
                        <tr
                          key={head.aggregate_id}
                          onClick={() => {
                            setCreating(false)
                            setViewVersion(0)
                            setSelectedId(head.aggregate_id)
                          }}
                          style={active ? { background: 'rgba(200,169,106,0.06)' } : undefined}
                        >
                          <td>
                            <div className="mono">{aggregateShort(head.aggregate_id)}</div>
                            <div className="dim" style={{ fontSize: 11 }}>{head.aggregate_type}</div>
                          </td>
                          <td>
                            {active && detail ? (
                              <span className="chip gold">{STATUS_LABEL[detail.state.status] ?? detail.state.status}</span>
                            ) : (
                              <span className="chip">คลิกเพื่อดูสถานะ</span>
                            )}
                          </td>
                          <td className="num">{number(head.current_version)}</td>
                          <td>{thaiDateTime(head.updated_at)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {detailQ.isLoading ? (
            <Skel h={240} />
          ) : detailQ.isError || !detail ? (
            <Empty title="โหลดสถานะบัญชีไม่ได้" body="ข้อมูลอาจถูกจำกัดสิทธิ์ หรือเซิร์ฟเวอร์ไม่พร้อม" />
          ) : (
            <>
              <div className="card card-pad">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <div>
                    <div className="card-label">สถานะที่ประกอบใหม่</div>
                    <div className="mono" style={{ marginTop: 8 }}>{detail.state.aggregate_id}</div>
                  </div>
                  <div className="row" style={{ gap: 8 }}>
                    <span className={`chip ${detail.target_version < detail.current_version ? 'gold' : ''}`}>
                      {detail.target_version < detail.current_version
                        ? `เวอร์ชัน ${detail.target_version} จาก ${detail.current_version}`
                        : `เวอร์ชัน ${detail.target_version}`}
                    </span>
                    {viewVersion > 0 ? (
                      <button className="btn" type="button" onClick={() => setViewVersion(0)}>
                        กลับไปสถานะล่าสุด
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="grid-3" style={{ marginTop: 16, gap: 12 }}>
                  <div>
                    <div className="dim" style={{ fontSize: 11 }}>ผู้ถือบัญชี</div>
                    <div style={{ marginTop: 4 }}>{detail.state.owner || '—'}</div>
                  </div>
                  <div>
                    <div className="dim" style={{ fontSize: 11 }}>ยอดคงเหลือ</div>
                    <div className="tnum" style={{ marginTop: 4, fontSize: 18 }}>
                      {money(detail.state.balance_satang)}
                    </div>
                  </div>
                  <div>
                    <div className="dim" style={{ fontSize: 11 }}>สถานะ</div>
                    <div style={{ marginTop: 4 }}>
                      <span className="chip gold">{STATUS_LABEL[detail.state.status] ?? detail.state.status}</span>
                    </div>
                  </div>
                </div>

                <div className="kv" style={{ marginTop: 18 }}>
                  <dt>เหตุการณ์ล่าสุด</dt>
                  <dd>{EVENT_LABEL[detail.state.last_event_type] ?? (detail.state.last_event_type || '—')}</dd>
                  <dt>รหัสเหตุการณ์</dt>
                  <dd className="mono">{detail.state.last_event_id || '—'}</dd>
                  <dt>จุดตั้งต้น replay</dt>
                  <dd>
                    {detail.snapshot_version > 0
                      ? `snapshot เวอร์ชัน ${detail.snapshot_version} + ${detail.events_replayed} เหตุการณ์`
                      : `เริ่มจากเวอร์ชัน 1 (${detail.events_replayed} เหตุการณ์)`}
                  </dd>
                  <dt>เวลาของสถานะ</dt>
                  <dd>{detail.state.updated_at ? thaiDateTime(detail.state.updated_at) : '—'}</dd>
                </div>
                <div className="dim" style={{ fontSize: 11, marginTop: 14 }}>{detail.note}</div>
              </div>

              <div className="card card-pad">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <div className="card-label">บันทึกเหตุการณ์</div>
                  <span className="dim" style={{ fontSize: 11 }}>
                    คลิกแถวเพื่อดูสถานะ ณ เวอร์ชันนั้น
                  </span>
                </div>
                {events.length === 0 ? (
                  <div style={{ marginTop: 14 }}>
                    <Empty title="ยังไม่มีเหตุการณ์" body="บัญชีนี้ยังไม่มีเหตุการณ์ที่บันทึก" />
                  </div>
                ) : (
                  <div className="table-wrap" style={{ marginTop: 14 }}>
                    <table className="data">
                      <thead>
                        <tr>
                          <th className="num">เวอร์ชัน</th>
                          <th>เหตุการณ์</th>
                          <th>รายละเอียด</th>
                          <th>เวลา</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.map((event) => (
                          <tr
                            key={event.id}
                            onClick={() => setViewVersion(event.version)}
                            style={viewVersion === event.version ? { background: 'rgba(200,169,106,0.06)' } : undefined}
                          >
                            <td className="num">{number(event.version)}</td>
                            <td>{EVENT_LABEL[event.type] ?? event.type}</td>
                            <td>{eventSummary(event)}</td>
                            <td>{thaiDateTime(event.occurred_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {replayResult ? (
            <div className="card card-pad">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <div className="card-label">ผลการ replay ที่บันทึกไว้</div>
                <span className="chip gold">{REPLAY_STATUS_LABEL[replayResult.status] ?? replayResult.status}</span>
              </div>
              {replayNotice ? <div className="dim" style={{ fontSize: 12, marginTop: 10 }}>{replayNotice}</div> : null}
              {replayResult.state ? (
                <div className="grid-3" style={{ marginTop: 14, gap: 12 }}>
                  <div>
                    <div className="dim" style={{ fontSize: 11 }}>เวอร์ชันที่ประกอบได้</div>
                    <div className="tnum" style={{ marginTop: 4 }}>{replayResult.state.version}</div>
                  </div>
                  <div>
                    <div className="dim" style={{ fontSize: 11 }}>ยอดคงเหลือ</div>
                    <div className="tnum" style={{ marginTop: 4 }}>{money(replayResult.state.balance_satang)}</div>
                  </div>
                  <div>
                    <div className="dim" style={{ fontSize: 11 }}>สถานะ</div>
                    <div style={{ marginTop: 4 }}>{STATUS_LABEL[replayResult.state.status] ?? replayResult.state.status}</div>
                  </div>
                </div>
              ) : null}
              <div className="dim" style={{ fontSize: 11, marginTop: 12 }}>
                งาน {aggregateShort(replayResult.id)} · เริ่มจากเวอร์ชัน {replayResult.from_version || 1} ·
                ใส่เหตุการณ์ {replayResult.events_replayed} รายการ · หัวปัจจุบัน {replayResult.current_version}
              </div>
            </div>
          ) : null}
        </div>

        <div className="stack">
          <div className="card card-pad">
            <div className="card-label">เพิ่มเหตุการณ์</div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
              {creating
                ? `บัญชีใหม่ ${aggregateShort(newAggregateId)}`
                : selectedHead
                  ? `บัญชี ${aggregateShort(selectedHead.aggregate_id)} · เวอร์ชันปัจจุบัน ${selectedHead.current_version}`
                  : 'ยังไม่ได้เลือกบัญชี'}
            </div>

            <form
              style={{ marginTop: 14 }}
              onSubmit={(e) => {
                e.preventDefault()
                if (canManage) appendEvent.mutate()
              }}
            >
              <div className="field" style={{ marginTop: 0 }}>
                <label htmlFor="es-type">ชนิดเหตุการณ์</label>
                <select
                  id="es-type"
                  className="input"
                  value={eventType}
                  disabled={!canManage}
                  onChange={(e) => setEventType(e.target.value as EventType)}
                >
                  {EVENT_TYPES.map((row) => (
                    <option key={row.id} value={row.id}>{row.label}</option>
                  ))}
                </select>
                <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
                  {EVENT_TYPES.find((row) => row.id === eventType)?.hint}
                </div>
              </div>

              {eventType === 'account.opened' ? (
                <div className="field">
                  <label htmlFor="es-owner">ชื่อผู้ถือบัญชี</label>
                  <input
                    id="es-owner"
                    className="input"
                    value={owner}
                    disabled={!canManage}
                    onChange={(e) => setOwner(e.target.value)}
                    placeholder="บริษัท ตัวอย่าง จำกัด"
                  />
                </div>
              ) : null}

              {showAmount ? (
                <div className="field">
                  <label htmlFor="es-amount">{amountLabel}</label>
                  <input
                    id="es-amount"
                    className="input mono"
                    inputMode="decimal"
                    value={amountBaht}
                    disabled={!canManage}
                    onChange={(e) => setAmountBaht(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              ) : null}

              <div className="field">
                <label htmlFor="es-expected">expected_version</label>
                <input
                  id="es-expected"
                  className="input mono"
                  inputMode="numeric"
                  value={expectedVersion}
                  disabled={!canManage}
                  onChange={(e) => setExpectedVersion(e.target.value)}
                />
                <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
                  ปล่อยให้เป็นเวอร์ชันล่าสุด หรือแก้เป็นค่าเก่าเพื่อทดสอบว่า API ตอบ 409
                </div>
              </div>

              <button
                className="btn btn-primary"
                type="submit"
                style={{ width: '100%', marginTop: 16 }}
                disabled={!canManage || appendEvent.isPending || (!creating && !selectedId)}
              >
                {appendEvent.isPending ? 'กำลังบันทึก…' : 'บันทึกเหตุการณ์'}
              </button>
              {!canManage ? (
                <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
                  เฉพาะเจ้าของ ผู้ดูแลระบบ และผู้จัดการตรวจสอบเท่านั้นที่เพิ่มเหตุการณ์ได้
                </div>
              ) : null}
              {appendEvent.isError ? <div className="form-error">{errorText(appendEvent.error, 'บันทึกไม่สำเร็จ')}</div> : null}
              {appendNotice ? (
                <div
                  style={{
                    marginTop: 12, padding: '10px 12px', border: '1px solid var(--color-hairline)',
                    borderRadius: 8, fontSize: 12, color: 'var(--color-ivory-muted)',
                  }}
                >
                  {appendNotice}
                </div>
              ) : null}
            </form>

            <div className="dim" style={{ fontSize: 11, marginTop: 14 }}>
              รหัสที่ใช้: <span className="mono">{formAggregateID ? aggregateShort(formAggregateID) : '—'}</span>
              {formAggregateID ? <span className="mono"> · {formAggregateID}</span> : null}
            </div>
          </div>

          <div className="card card-pad">
            <div className="card-label">ย้อนสถานะ (replay)</div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
              ประกอบสถานะจากเหตุการณ์เวอร์ชัน 1 ถึงเวอร์ชันเป้าหมาย แล้วบันทึกผลไว้เทียบกับหัวปัจจุบัน
            </div>
            <form
              style={{ marginTop: 14 }}
              onSubmit={(e) => {
                e.preventDefault()
                if (canManage) replay.mutate()
              }}
            >
              <div className="field" style={{ marginTop: 0 }}>
                <label htmlFor="es-target">เวอร์ชันเป้าหมาย</label>
                <input
                  id="es-target"
                  className="input mono"
                  inputMode="numeric"
                  value={replayTarget}
                  disabled={!canManage}
                  onChange={(e) => setReplayTarget(e.target.value)}
                />
                <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
                  {detail
                    ? `อ่านได้ตั้งแต่เวอร์ชัน 1 ถึง ${detail.current_version}`
                    : 'เลือกบัญชีก่อน'}
                </div>
              </div>
              <button
                className="btn btn-primary"
                type="submit"
                style={{ width: '100%', marginTop: 14 }}
                disabled={!canManage || replay.isPending || !selectedId}
              >
                {replay.isPending ? 'กำลังประกอบสถานะ…' : 'เริ่ม replay'}
              </button>
              {replay.isError ? <div className="form-error">{errorText(replay.error, 'replay ไม่สำเร็จ')}</div> : null}
            </form>
          </div>

          <div className="card card-pad">
            <div className="card-label">รายการ replay ล่าสุด</div>
            {replaysQ.data && replaysQ.data.jobs.length > 0 ? (
              <div className="stack" style={{ gap: 10, marginTop: 12 }}>
                {replaysQ.data.jobs.slice(0, 8).map((job) => (
                  <div
                    key={job.id}
                    className="row"
                    style={{ justifyContent: 'space-between', fontSize: 12, gap: 8 }}
                  >
                    <div>
                      <div className="mono">{aggregateShort(job.aggregate_id)} · v{job.target_version}</div>
                      <div className="dim" style={{ fontSize: 11 }}>{thaiDateTime(job.created_at)}</div>
                    </div>
                    <div className="row" style={{ gap: 6 }}>
                      {job.state ? <span className="tnum">{money(job.state.balance_satang)}</span> : null}
                      <span className="chip">{REPLAY_STATUS_LABEL[job.status] ?? job.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="dim" style={{ fontSize: 12, marginTop: 10 }}>ยังไม่มีงาน replay</div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
